/**
 * CCM care plan time (BRD-CLIN-2026-008): what a nurse picked from the patient's care plans for one
 * CCM time row, and the care coordination or other work that no care plan covers. The request shapes
 * mirror welby-micro-service's `care-plan-work.model.ts`; the server reads every text itself, so the time
 * form sends ids, ticks and notes only.
 */

/** One plan the nurse picked from, sent in the time body as `care_plan_work`. */
export interface CarePlanWorkRequestPlan {
  care_plan_id: string;
  /** One line on what the nurse reviewed or did on this plan. Required for every plan picked from. */
  note: string;
  items: CarePlanWorkRequestItem[];
}

/** One goal: its goal box, its interventions box, or both. */
export interface CarePlanWorkRequestItem {
  problem_id: string;
  goal_id: string;
  goal: boolean;
  interventions: boolean;
}

/** The two items of a CCM row that no care plan covers, in the order the picker shows them. */
export const CARE_PLAN_WORK_ACTIVITIES = ['care_coordination', 'other'] as const;
export type CarePlanWorkActivityKey = (typeof CARE_PLAN_WORK_ACTIVITIES)[number];

/** Care coordination or other work, ticked: what the nurse did, in one line. Required, like a plan's note. */
export interface CarePlanWorkActivity {
  note: string;
}

/**
 * What the time form sends in `care_plan_work` for a CCM row, and the value of the row's picker: the plans
 * picked from, and each item no care plan covers, `null` when it is not ticked.
 */
export interface CarePlanWorkRequest extends Record<CarePlanWorkActivityKey, CarePlanWorkActivity | null> {
  plans: CarePlanWorkRequestPlan[];
}

/** What `GET .../clinical-time/:id/care-plan-work` returns: the work as it was frozen when the entry was saved. */
export interface CarePlanWorkRecord extends Partial<Record<CarePlanWorkActivityKey, CarePlanWorkActivity | null>> {
  time_id: string;
  patient_id: string;
  client_id: string;
  saved_by: string;
  saved_at: string | Date;
  time_stamp: string | Date;
  /** Empty when the entry holds only care coordination or other work. */
  plans: CarePlanWorkPlanSnapshot[];
}

export interface CarePlanWorkPlanSnapshot {
  care_plan_id: string;
  name: string;
  dx_list: string[];
  note: string;
  items: CarePlanWorkItemSnapshot[];
}

export interface CarePlanWorkItemSnapshot {
  problem_id: string;
  problem_title: string;
  goal_id: string;
  goal_text: string;
  goal: boolean;
  interventions: boolean;
  interventions_snapshot: { task_id: string; name: string; content: string[] }[];
}

/** The `code` of a 400 the server answers a refused CCM row with. */
export type CarePlanWorkErrorCode =
  | 'REQUIRED'
  | 'NOTE_REQUIRED'
  | 'NOTE_TOO_LONG'
  | 'EMPTY_ITEM'
  | 'TOO_MANY'
  | 'MALFORMED'
  | 'PLAN_NOT_ACTIVE'
  | 'GOAL_NOT_OPEN'
  | 'NO_INTERVENTIONS'
  | 'PATIENT_CHANGED'
  | 'INVALID_TIME_ID';

/** A care plan as the picker shows it: one section per plan. */
export interface CarePlanPickerPlan {
  carePlanId: string;
  name: string;
  dxList: string[];
  goals: CarePlanPickerGoal[];
  /**
   * True for a plan that is no longer Active but still holds goals picked on this entry. It is shown
   * from the frozen copy so an edit keeps those picks; its goals can be unticked, never newly ticked.
   */
  frozen: boolean;
}

/** One goal with its interventions, as one goal box and one interventions box. */
export interface CarePlanPickerGoal {
  problemId: string;
  problemTitle: string;
  goalId: string;
  goalText: string;
  /** Every line of every intervention of the goal, in order. */
  interventions: string[];
  /** True when the goal comes from the frozen copy because it is gone from today's care plan. */
  frozen: boolean;
  /** For a frozen goal: what was ticked when it was saved, the only boxes it may keep. */
  frozenGoal?: boolean;
  frozenInterventions?: boolean;
}

/** What the time form needs to show the picker for one row: the sections, and the picks to start from. */
export interface CarePlanPickerData {
  plans: CarePlanPickerPlan[];
  value: CarePlanWorkRequest;
  /**
   * The entry's record, or `null`. An entry already CCM before go live with no record may be edited
   * without picks (FR-5.3), so the time form uses this to decide whether the picker is required.
   */
  record: CarePlanWorkRecord | null;
}

/** Whether the picker's data is ready. Until it is, the picker shows why and keeps its row invalid. */
export type CarePlanPickerStatus = 'loading' | 'ready' | 'error';
